/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useRef } from "react"
import { Box, Button, TextField, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem, ListItemText, Divider, Alert, Snackbar, Select } from "@mui/material"
import { useParams, useNavigate } from "react-router-dom"
import { getDocs, collection, query, where, QuerySnapshot, onSnapshot } from "firebase/firestore"
import { db } from "../../firebase"
import ShippingDestinationForm, { ShipmentProductSnapshot } from "./lib/ShippingDestinationForm"
import DeleteIcon from '@mui/icons-material/Delete'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import SaveIcon from '@mui/icons-material/Save'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import DescriptionIcon from '@mui/icons-material/Description'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { BarcodeFormat } from '@zxing/library'

interface IPartPrice {
  id: string
  part: string
  priceUnit: 'perPiece' | 'perGram'
  frozenPrice: number
  chilledPrice: number
  taxRate?: 8 | 10
  description: string
}


const SearchProduct = () => {
  const urlParams = useParams<{ productNo: string }>()
  const navigate = useNavigate()
  const [productNo, setProductNo] = useState<string>('')
  const [productList, setProductList] = useState<any[]>([])
  const [selectedProducts, setSelectedProducts] = useState<string[]>([])
  const [openShipping, setOpenShipping] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [soldShipmentId, setSoldShipmentId] = useState<string | null>(null)

  // バーコード読み取り関連の状態
  const [openBarcodeScanner, setOpenBarcodeScanner] = useState<boolean>(false)
  const [cameraError, setCameraError] = useState<string>('')
  const [isScanning, setIsScanning] = useState<boolean>(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null)

  // SessionStorage保存・読込関連の状態
  const [openSaveDialog, setOpenSaveDialog] = useState<boolean>(false)
  const [saveName, setSaveName] = useState<string>('')
  const [saveError, setSaveError] = useState<string>('')
  const [anchorElLoad, setAnchorElLoad] = useState<null | HTMLElement>(null)
  const [savedLists, setSavedLists] = useState<string[]>([])
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)
  const [snackbarMessage, setSnackbarMessage] = useState<string>('')
  const [partPrices, setPartPrices] = useState<IPartPrice[]>([])
  const [productQuantities, setProductQuantities] = useState<{ [productId: string]: number }>({})

  // 部位別価格マスタの取得
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'partPrices'), (snapshot) => {
      const prices = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      } as IPartPrice))
      setPartPrices(prices)
    })
    return unsubscribe
  }, [])

  // 価格を計算する関数（個数を考慮）
  const calculatePrice = (product: any, quantity: number = 1): number => {
    const partPrice = partPrices.find(p => p.part === product.part)
    if (!partPrice) return 0

    // 重量をグラムから取得（文字列の場合は数値に変換）
    const weightInGrams = typeof product.weight === 'string' ?
      parseFloat(product.weight.replace(/[^\d.-]/g, '')) || 0 :
      (typeof product.weight === 'number' ? product.weight : 0)

    // グラムをキログラムに変換
    const weightInKg = weightInGrams / 1000

    // 温度状態に応じた価格を取得（未設定の場合は冷凍）
    const pricePerUnit = (product.storageType === '冷凍' || !product.storageType) ? partPrice.frozenPrice : partPrice.chilledPrice

    // 価格単位に応じて計算
    if (partPrice.priceUnit === 'perPiece') {
      // 1個あたりの価格×個数（四捨五入）
      return Math.round(pricePerUnit * quantity)
    } else {
      // kg単位の価格（グラムをkgに変換してから掛けて四捨五入）
      return Math.round(pricePerUnit * weightInKg)
    }
  }

  // 合計重量を計算する関数（個数を考慮）
  const calculateTotalWeight = (products: any[]) => {
    return products.reduce((total, product) => {
      const weight = parseFloat(product.weight) || 0
      // perPiece（個数単位）の商品の場合は個数を掛ける
      const quantity = isPerPiece(product) ? (productQuantities[product.id] || 1) : 1
      return total + (weight * quantity)
    }, 0)
  }

  // 商品1個の重量を取得する関数
  const getProductWeight = (product: any): number => {
    return parseFloat(product.weight) || 0
  }

  // 表示用の重量を計算する関数（個数を考慮）
  const getDisplayWeight = (product: any): number => {
    const weight = getProductWeight(product)
    const quantity = isPerPiece(product) ? (productQuantities[product.id] || 1) : 1
    return weight * quantity
  }

  // 選択された商品の合計重量を計算する関数
  const calculateSelectedWeight = () => {
    const selectedProductsData = productList.filter(product => selectedProducts.includes(product.id))
    return calculateTotalWeight(selectedProductsData)
  }

  // 合計金額を計算する関数（個数を考慮）
  const calculateTotalPrice = (products: any[]) => {
    if (partPrices.length === 0) return 0
    return products.reduce((total, product) => {
      const quantity = productQuantities[product.id] || 1
      const price = calculatePrice(product, quantity)
      return total + price
    }, 0)
  }

  // 選択された商品の合計金額を計算する関数
  const calculateSelectedPrice = () => {
    const selectedProductsData = productList.filter(product => selectedProducts.includes(product.id))
    return calculateTotalPrice(selectedProductsData)
  }

  useEffect(() => {
    let inputNum = new Array<number>()
    const handleInput = (event: any) => {
      const checkNum = /^[0-9]+$/;
      if (checkNum.test(event.key)) {
        inputNum.push(event.key)
      }
      if (inputNum.length === 13) {
        setProductNo(inputNum.join(''))
        search(inputNum.join(''))
        inputNum = new Array<number>()
      }
    };

    document.addEventListener('keydown', handleInput);

    return () => {
      document.removeEventListener('keydown', handleInput);
    };
  }, []);

  useEffect(() => {
    const no = urlParams.productNo
    if (!no) return
    setProductNo(no)
    search(no)
  }, [urlParams])

  // 見積りプレビュー等の別画面から戻ってきた際に商品リストを復元する
  useEffect(() => {
    const raw = sessionStorage.getItem(CURRENT_LIST_KEY)
    if (!raw) return
    try {
      const saved = JSON.parse(raw)
      if (Array.isArray(saved.products) && saved.products.length > 0) {
        setProductList(saved.products)
        setProductQuantities(saved.quantities || {})
        setSelectedProducts(saved.selectedProducts || [])
      }
    } catch (e) {
      console.error('商品リストの復元に失敗:', e)
    }
  }, [])

  // 商品リストの変更を自動的にSessionStorageへ保存する（画面遷移して戻ってきても復元できるように）
  useEffect(() => {
    if (productList.length === 0) {
      sessionStorage.removeItem(CURRENT_LIST_KEY)
      return
    }
    sessionStorage.setItem(CURRENT_LIST_KEY, JSON.stringify({
      products: productList,
      quantities: productQuantities,
      selectedProducts,
    }))
  }, [productList, productQuantities, selectedProducts])

  // 出荷済み商品の出荷情報リンク先を特定する
  // 新しい出荷データは products ドキュメントに shipmentId を直接保持しているのでそれを使う。
  // shipmentId が無い過去データは、出荷先・出荷日で絞り込んだ上でshipments内のproducts配列を照合する。
  const findShipmentIdForProduct = (product: any) => {
    if (product.shipmentId) {
      setSoldShipmentId(product.shipmentId)
      return
    }
    if (!product.customer || !product.shippingDate) return
    getDocs(query(
      collection(db, 'shipments'),
      where('customer', '==', product.customer),
      where('shippingDate', '==', product.shippingDate)
    )).then((snap) => {
      const match = snap.docs.find((d) =>
        (d.data().products || []).some((p: any) => p.id === product.id)
      )
      if (match) setSoldShipmentId(match.id)
    })
  }

  const search = (productNo: string) => {
    if(!productNo) return
    setErrorMessage('')
    setSoldShipmentId(null)
    getDocs(query(collection(db, 'products'), where('productNo', '==', productNo))).then((_: QuerySnapshot) => {
      const [doc] = _.docs
      if (doc) {
        const newProduct: any = { ...doc.data(), id: doc.id }
        if (newProduct.isSold) {
          // 出荷済みの商品は一覧に追加せず、出荷先情報を案内するのみにする
          setErrorMessage(
            `この商品は既に出荷済みです（出荷先: ${newProduct.customer || '不明'}、出荷日: ${newProduct.shippingDate || '不明'}）`
          )
          findShipmentIdForProduct(newProduct)
          return
        }
        // 既に同じ商品が追加されていないか確認
        const exists = productList.some(p => p.id === doc.id)
        if (!exists) {
          setProductList(prev => [...prev, newProduct])
          setProductQuantities(prev => ({ ...prev, [doc.id]: 1 }))
        } else {
          setErrorMessage('この商品は既にリストに追加されています')
        }
      } else {
        setErrorMessage('該当する商品情報がありません')
      }
    }).finally(() => {
      setProductNo('')
    })
  }

  // 一覧の商品ごとに温度状態を変更
  const handleStorageTypeChange = (productId: string, storageType: string) => {
    setProductList((prev) => prev.map((p) => (p.id === productId ? { ...p, storageType } : p)))
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // 未出荷の商品のみを選択
      const unsoldProductIds = productList
        .filter(product => !product.isSold)
        .map(product => product.id)
      setSelectedProducts(unsoldProductIds)
    } else {
      setSelectedProducts([])
    }
  }

  const handleSelect = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, id])
    } else {
      setSelectedProducts(prev => prev.filter(productId => productId !== id))
    }
  }

  const removeProduct = (id: string) => {
    setProductList(prev => prev.filter(product => product.id !== id))
    setSelectedProducts(prev => prev.filter(productId => productId !== id))
    // 個数情報も削除
    setProductQuantities(prev => {
      const newQuantities = { ...prev }
      delete newQuantities[id]
      return newQuantities
    })
  }

  const clearAll = () => {
    setProductList([])
    setSelectedProducts([])
    setProductQuantities({})
  }

  // 個数変更ハンドラー
  const handleQuantityChange = (productId: string, value: string) => {
    const quantity = parseInt(value) || 1
    if (quantity > 0 && quantity <= 9999) {
      setProductQuantities(prev => ({ ...prev, [productId]: quantity }))
    }
  }

  // perPieceかどうか判定する関数
  const isPerPiece = (product: any): boolean => {
    const partPrice = partPrices.find(p => p.part === product.part)
    return partPrice?.priceUnit === 'perPiece'
  }

  // 部位に設定された消費税率を取得する関数（未設定の部位は8%とみなす）
  const getTaxRate = (product: any): number => {
    const partPrice = partPrices.find(p => p.part === product.part)
    return partPrice?.taxRate === 10 ? 10 : 8
  }

  const onChange = ({ target: { value } }: any) => {
    setProductNo(value)
  }

  // バーコードスキャンを開始する関数
  const startBarcodeScanning = async () => {
    setOpenBarcodeScanner(true)
    setCameraError('')
    setIsScanning(true)

    try {
      // コードリーダーを初期化（一次元・二次元バーコード両方対応）
      // BrowserMultiFormatReaderはデフォルトで以下をサポート:
      // - QRコード、Data Matrix (二次元)
      // - EAN-13, EAN-8, UPC-A, UPC-E, Code128, Code39, ITF等 (一次元)
      const codeReader = new BrowserMultiFormatReader()
      codeReaderRef.current = codeReader

      // カメラデバイスを取得してスキャンを開始
      const videoInputDevices = await BrowserMultiFormatReader.listVideoInputDevices()

      if (videoInputDevices.length === 0) {
        throw new Error('カメラが見つかりません')
      }

      // モバイルの背面カメラを優先的に選択
      let selectedDeviceId = videoInputDevices[0].deviceId
      const backCamera = videoInputDevices.find(device =>
        device.label.toLowerCase().includes('back') ||
        device.label.toLowerCase().includes('environment')
      )
      if (backCamera) {
        selectedDeviceId = backCamera.deviceId
      }

      if (videoRef.current) {
        await codeReader.decodeFromVideoDevice(
          selectedDeviceId,
          videoRef.current,
          (result) => {
            if (result) {
              // バーコードが読み取れた場合
              const scannedCode = result.getText()
              const format = result.getBarcodeFormat()
              console.log(`Detected barcode format: ${BarcodeFormat[format]}, Code: ${scannedCode}`)
              stopBarcodeScanning()
              search(scannedCode)
            }
          }
        )
      }
    } catch (err: any) {
      console.error('Barcode scanner error:', err)
      setCameraError('カメラの起動に失敗しました: ' + err.message)
      setIsScanning(false)
    }
  }

  // バーコードスキャンを停止する関数
  const stopBarcodeScanning = () => {
    // ビデオストリームを停止
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream
      stream.getTracks().forEach(track => track.stop())
      videoRef.current.srcObject = null
    }

    if (codeReaderRef.current) {
      codeReaderRef.current = null
    }

    setIsScanning(false)
    setOpenBarcodeScanner(false)
    setCameraError('')
  }

  // ダイアログが閉じられる時の処理
  const handleCloseBarcodeScanner = () => {
    stopBarcodeScanning()
  }

  // SessionStorageのキープレフィックス
  const STORAGE_PREFIX = 'productList_'
  // 画面遷移して戻ってきた際に商品リストを復元するための自動保存キー
  const CURRENT_LIST_KEY = 'productSearchCurrentList'

  // 保存されているリスト一覧を取得
  const getSavedListNames = (): string[] => {
    const keys: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(STORAGE_PREFIX)) {
        keys.push(key.replace(STORAGE_PREFIX, ''))
      }
    }
    return keys.sort()
  }

  // 保存ダイアログを開く
  const handleOpenSaveDialog = () => {
    if (productList.length === 0) {
      setSnackbarMessage('保存する商品がありません')
      setSnackbarOpen(true)
      return
    }
    setSaveName('')
    setSaveError('')
    setOpenSaveDialog(true)
  }

  // 保存ダイアログを閉じる
  const handleCloseSaveDialog = () => {
    setOpenSaveDialog(false)
    setSaveName('')
    setSaveError('')
  }

  // 商品リストをSessionStorageに保存
  const handleSaveList = () => {
    if (!saveName.trim()) {
      setSaveError('保存名を入力してください')
      return
    }

    try {
      const saveData = {
        products: productList,
        quantities: productQuantities,
        savedAt: new Date().toISOString(),
        count: productList.length
      }
      sessionStorage.setItem(STORAGE_PREFIX + saveName.trim(), JSON.stringify(saveData))
      setSnackbarMessage(`「${saveName.trim()}」として保存しました`)
      setSnackbarOpen(true)
      handleCloseSaveDialog()
    } catch (error) {
      setSaveError('保存に失敗しました')
      console.error('Save error:', error)
    }
  }

  // 読込メニューを開く
  const handleOpenLoadMenu = (event: React.MouseEvent<HTMLElement>) => {
    const lists = getSavedListNames()
    if (lists.length === 0) {
      setSnackbarMessage('保存されているリストがありません')
      setSnackbarOpen(true)
      return
    }
    setSavedLists(lists)
    setAnchorElLoad(event.currentTarget)
  }

  // 読込メニューを閉じる
  const handleCloseLoadMenu = () => {
    setAnchorElLoad(null)
  }

  // 保存されたリストを読み込む
  const handleLoadList = (name: string) => {
    try {
      const data = sessionStorage.getItem(STORAGE_PREFIX + name)
      if (data) {
        const saveData = JSON.parse(data)
        setProductList(saveData.products || [])
        setSelectedProducts([])
        // 個数情報も復元（保存されていない場合はデフォルト1）
        const quantities: { [key: string]: number } = {}
        saveData.products.forEach((product: any) => {
          quantities[product.id] = saveData.quantities?.[product.id] || 1
        })
        setProductQuantities(quantities)
        setSnackbarMessage(`「${name}」を読み込みました（${saveData.count}件）`)
        setSnackbarOpen(true)
      }
    } catch (error) {
      setSnackbarMessage('読み込みに失敗しました')
      setSnackbarOpen(true)
      console.error('Load error:', error)
    }
    handleCloseLoadMenu()
  }

  // 保存されたリストを削除
  const handleDeleteSavedList = (name: string, event: React.MouseEvent) => {
    event.stopPropagation()
    if (window.confirm(`「${name}」を削除しますか？`)) {
      sessionStorage.removeItem(STORAGE_PREFIX + name)
      const updatedLists = getSavedListNames()
      setSavedLists(updatedLists)
      setSnackbarMessage(`「${name}」を削除しました`)
      setSnackbarOpen(true)
      if (updatedLists.length === 0) {
        handleCloseLoadMenu()
      }
    }
  }

  // Snackbarを閉じる
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  // 出荷登録完了時の処理
  const handleShippingComplete = () => {
    // 現在のproductListと一致する保存済みリストを検索して削除
    const savedListNames = getSavedListNames()
    let deletedCount = 0

    savedListNames.forEach((name) => {
      const data = sessionStorage.getItem(STORAGE_PREFIX + name)
      if (data) {
        try {
          const saveData = JSON.parse(data)
          const savedProducts = saveData.products || []

          // 保存されている商品リストのIDセット
          const savedProductIds = new Set(savedProducts.map((p: any) => p.id))
          // 現在の商品リストのIDセット
          const currentProductIds = new Set(productList.map(p => p.id))

          // IDが完全に一致する場合、そのリストを削除
          if (
            savedProductIds.size === currentProductIds.size &&
            Array.from(savedProductIds).every(id => currentProductIds.has(id))
          ) {
            sessionStorage.removeItem(STORAGE_PREFIX + name)
            deletedCount++
          }
        } catch (e) {
          console.error('Error comparing saved list:', e)
        }
      }
    })

    if (deletedCount > 0) {
      setSnackbarMessage(`出荷登録が完了し、${deletedCount}件の保存済みリストを削除しました`)
      setSnackbarOpen(true)
    }
  }

  return (
    <Box display="contents" justifyItems="center">
      <Box display="flex" justifyContent="center" mt={2}>
        <Typography variant="h4">商品番号検索</Typography>
      </Box>
      <Box display="flex" justifyContent="center" alignContent="center" mt={2}>
        <TextField
          type="number"
          id="productNo"
          name="productNo"
          label="商品番号"
          onChange={onChange}
          value={productNo}
          inputProps={{
            inputMode: "numeric", pattern: "[0-9]*"
          }}
          sx={{ width: 200 }}
        />
      </Box>
      <Box display="flex-col" m={1} justifyItems="center">
        <Box display="flex" justifyContent="center">
          <Typography variant="subtitle2" color="red">{errorMessage}</Typography>
        </Box>
        {soldShipmentId && (
          <Box display="flex" justifyContent="center" mt={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(`/admin/shipments/${soldShipmentId}`)}
            >
              出荷情報を見る
            </Button>
          </Box>
        )}
        <Box display="flex" justifyContent="center" mt={2} gap={1}>
          <Button variant="contained" color="secondary" onClick={() => setProductNo('')}>クリア</Button>
          <Button variant="contained" onClick={() => search(productNo)}>検索</Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<QrCodeScannerIcon />}
            onClick={startBarcodeScanning}
            disabled={isScanning}
          >
            バーコード読み取り
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<FolderOpenIcon />}
            onClick={handleOpenLoadMenu}
          >
            リストを読込
          </Button>
        </Box>
      </Box>

      {productList.length > 0 && (
        <Box width="90%" mx="auto" mt={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">商品一覧</Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                color="info"
                startIcon={<SaveIcon />}
                onClick={handleOpenSaveDialog}
              >
                リストを保存
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenShipping(true)}
                disabled={selectedProducts.length === 0}
              >
                選択した商品の出荷先登録
              </Button>
              <Button
                variant="contained"
                color="info"
                startIcon={<DescriptionIcon />}
                onClick={() => {
                  const targets = productList.filter((p) => selectedProducts.includes(p.id))
                  const snapshots = targets.map((p) => {
                    const quantity = isPerPiece(p) ? (productQuantities[p.id] || 1) : 1
                    return {
                      id: p.id,
                      productNo: p.productNo,
                      individualNo: p.individualNo,
                      part: p.part,
                      weight: p.weight,
                      storageType: p.storageType,
                      quantity,
                      price: calculatePrice(p, quantity),
                      taxRate: getTaxRate(p),
                    }
                  })
                  const productsTotal = snapshots.reduce((sum, p) => sum + (p.price || 0), 0)
                  const totalWeight = snapshots.reduce((sum, p) => {
                    const w = typeof p.weight === 'string'
                      ? parseFloat(p.weight.replace(/[^\d.-]/g, '')) || 0
                      : (typeof p.weight === 'number' ? p.weight : 0)
                    return sum + w * (p.quantity || 1)
                  }, 0)
                  const totalCount = snapshots.reduce((sum, p) => sum + (p.quantity || 1), 0)
                  sessionStorage.setItem('quotationPreviewData', JSON.stringify({
                    products: snapshots,
                    totalCount,
                    totalWeight,
                    totalPrice: productsTotal,
                    customer: '',
                    shippingFee: 0,
                  }))
                  navigate('/admin/quotation/preview')
                }}
                disabled={selectedProducts.length === 0}
              >
                見積書を作成
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={clearAll}
              >
                リストをクリア
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedProducts.length > 0 && selectedProducts.length < productList.filter(p => !p.isSold).length}
                      checked={productList.length > 0 && selectedProducts.length === productList.filter(p => !p.isSold).length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>商品番号</TableCell>
                  <TableCell>個体識別番号</TableCell>
                  <TableCell>商品</TableCell>
                  <TableCell>部位</TableCell>
                  <TableCell>税率</TableCell>
                  <TableCell>重量(g)</TableCell>
                  <TableCell>温度状態</TableCell>
                  <TableCell>個数</TableCell>
                  <TableCell>価格（税込）</TableCell>
                  <TableCell>加工日</TableCell>
                  <TableCell>消費期限</TableCell>
                  <TableCell>出荷状況</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productList.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedProducts.includes(product.id)}
                        onChange={(e) => handleSelect(product.id, e.target.checked)}
                        disabled={product.isSold}
                      />
                    </TableCell>
                    <TableCell>{product.productNo}</TableCell>
                    <TableCell>{product.individualNo}</TableCell>
                    <TableCell>{product.type}</TableCell>
                    <TableCell>{product.part}</TableCell>
                    <TableCell>{getTaxRate(product)}%</TableCell>
                    <TableCell>
                      {isPerPiece(product) && (productQuantities[product.id] || 1) > 1
                        ? `${getDisplayWeight(product).toLocaleString()}g (${getProductWeight(product)}g × ${productQuantities[product.id] || 1})`
                        : `${product.weight}g`
                      }
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={product.storageType === 'チルド' ? 'チルド' : '冷凍'}
                        onChange={(e) => handleStorageTypeChange(product.id, e.target.value as string)}
                        disabled={product.isSold}
                        sx={{ minWidth: 90 }}
                      >
                        <MenuItem value="冷凍">冷凍</MenuItem>
                        <MenuItem value="チルド">チルド</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {isPerPiece(product) ? (
                        <TextField
                          type="number"
                          size="small"
                          value={productQuantities[product.id] || 1}
                          onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                          inputProps={{
                            min: 1,
                            max: 9999,
                            style: { width: '60px', textAlign: 'right' }
                          }}
                          disabled={product.isSold}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {partPrices.length > 0 ? (
                        `¥${calculatePrice(product, productQuantities[product.id] || 1).toLocaleString('ja-JP', { maximumFractionDigits: 0 })}`
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{product.processingDate}</TableCell>
                    <TableCell>{product.expirationDate}</TableCell>
                    <TableCell>{product.isSold ? '出荷済' : '未出荷'}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => removeProduct(product.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 合計重量・金額表示セクション */}
          <Box mt={3} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="h6" gutterBottom>
              重量・金額情報
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body1">
                  全商品合計重量: <strong>{calculateTotalWeight(productList).toLocaleString()}g</strong>
                </Typography>
                <Typography variant="body1" color="success.main">
                  全商品合計金額: <strong>¥{calculateTotalPrice(productList).toLocaleString('ja-JP', { maximumFractionDigits: 0 })}</strong>
                </Typography>
                {selectedProducts.length > 0 && (
                  <>
                    <Typography variant="body1" color="primary" mt={1}>
                      選択商品合計重量: <strong>{calculateSelectedWeight().toLocaleString()}g</strong>
                    </Typography>
                    <Typography variant="body1" color="primary">
                      選択商品合計金額: <strong>¥{calculateSelectedPrice().toLocaleString('ja-JP', { maximumFractionDigits: 0 })}</strong>
                    </Typography>
                  </>
                )}
              </Box>
              <Box textAlign="right">
                <Typography variant="body2" color="text.secondary">
                  商品数: {productList.length}件
                </Typography>
                {selectedProducts.length > 0 && (
                  <Typography variant="body2" color="primary">
                    選択数: {selectedProducts.length}件
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <ShippingDestinationForm
        productIds={selectedProducts}
        productSnapshots={
          productList
            .filter((p) => selectedProducts.includes(p.id))
            .map<ShipmentProductSnapshot>((p) => {
              const quantity = isPerPiece(p) ? (productQuantities[p.id] || 1) : 1
              return {
                id: p.id,
                productNo: p.productNo,
                individualNo: p.individualNo,
                part: p.part,
                weight: p.weight,
                storageType: p.storageType,
                quantity,
                price: calculatePrice(p, quantity),
                taxRate: getTaxRate(p),
              }
            })
        }
        isOpen={openShipping}
        onClose={() => setOpenShipping(false)}
        onShippingComplete={handleShippingComplete}
      />

      {/* バーコード読み取りダイアログ */}
      <Dialog
        open={openBarcodeScanner}
        onClose={handleCloseBarcodeScanner}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <QrCodeScannerIcon sx={{ mr: 1 }} />
            バーコード読み取り
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" p={2}>
            {cameraError ? (
              <Box textAlign="center">
                <Typography variant="body1" color="error" gutterBottom>
                  {cameraError}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  カメラへのアクセス許可が必要です。ブラウザの設定を確認してください。
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" gutterBottom textAlign="center">
                  バーコード（QRコード・JANコード等）をカメラに向けてください
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 300,
                    bgcolor: 'black',
                    borderRadius: 1,
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <video
                    ref={videoRef}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    playsInline
                    muted
                  />
                  {isScanning && !cameraError && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80%',
                        height: '60%',
                        border: '2px solid #fff',
                        borderRadius: 1,
                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.3)',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: 'linear-gradient(90deg, transparent, #ff0000, transparent)',
                          animation: 'scan 2s linear infinite'
                        }
                      }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
                  読み取りが完了すると自動的に検索が実行されます
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBarcodeScanner} variant="outlined">
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* スキャンアニメーション用のCSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes scan {
            0% { transform: translateY(0); }
            100% { transform: translateY(180px); }
          }
        `
      }} />

      {/* リスト保存ダイアログ */}
      <Dialog
        open={openSaveDialog}
        onClose={handleCloseSaveDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <SaveIcon sx={{ mr: 1 }} />
            商品リストを保存
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              現在の商品リスト（{productList.length}件）を任意の名前で保存できます
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="保存名"
              type="text"
              fullWidth
              value={saveName}
              onChange={(e) => {
                setSaveName(e.target.value)
                setSaveError('')
              }}
              error={!!saveError}
              helperText={saveError}
              placeholder="例: 2024年1月出荷分"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSaveDialog}>キャンセル</Button>
          <Button onClick={handleSaveList} variant="contained" color="primary">
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* リスト読込メニュー */}
      <Menu
        anchorEl={anchorElLoad}
        open={Boolean(anchorElLoad)}
        onClose={handleCloseLoadMenu}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: '320px',
          },
        }}
      >
        <Box px={2} py={1}>
          <Typography variant="subtitle2" color="text.secondary">
            保存されているリスト
          </Typography>
        </Box>
        <Divider />
        {savedLists.map((name) => {
          const data = sessionStorage.getItem(STORAGE_PREFIX + name)
          let count = 0
          let savedAt = ''
          if (data) {
            try {
              const parsed = JSON.parse(data)
              count = parsed.count || 0
              savedAt = parsed.savedAt ? new Date(parsed.savedAt).toLocaleString('ja-JP') : ''
            } catch (e) {
              console.error('Parse error:', e)
            }
          }
          return (
            <MenuItem key={name} onClick={() => handleLoadList(name)}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Box flex={1}>
                  <ListItemText
                    primary={name}
                    secondary={
                      <Box component="span">
                        <Typography variant="caption" display="block">
                          {count}件
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {savedAt}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteSavedList(name, e)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </MenuItem>
          )
        })}
      </Menu>

      {/* 通知用Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default SearchProduct
